import type { ReactNode } from 'react'

import { Link, useRouterState } from '@tanstack/react-router'
import clsx from 'clsx'

import styles from './PageTabs.module.scss'

export type PageTabItem = {
  /** Rota da seção. É a fonte do estado ativo — não existe prop de seleção. */
  to: string
  label: string
  /** Reforça o rótulo; nunca o substitui (ACCESSIBILITY.md §11). */
  icon?: ReactNode
}

type PageTabsProps = {
  items: PageTabItem[]
  className?: string
  'aria-label': string
}

/**
 * Navegação entre seções de uma mesma área, para o slot `Screen.Sticky`.
 *
 * Deliberadamente **não** usa o padrão ARIA de abas. `role="tablist"`/`tab`/
 * `tabpanel` descreve painéis que trocam dentro da mesma página; aqui cada
 * seção é uma rota, com mudança de URL e de foco. Anunciar "aba" ao leitor de
 * tela descreveria uma interação que não é a que acontece, e `role="tab"` num
 * link ainda desativaria a expectativa de comportamento de link (abrir em nova
 * aba, copiar endereço). Por isso: `<nav>` + `aria-current="page"`.
 *
 * Distinção com `FilterChips`: chips filtram um mesmo conjunto de conteúdo
 * (`role="group"` + `aria-pressed`); `PageTabs` navega entre conteúdos
 * distintos. Os dois não são intercambiáveis.
 */
export const PageTabs = ({
  items,
  className,
  'aria-label': ariaLabel
}: PageTabsProps) => {
  const pathname = useRouterState({ select: s => s.location.pathname })

  return (
    <nav className={clsx(styles.nav, className)} aria-label={ariaLabel}>
      {items.map(item => {
        const isActive = pathname === item.to

        return (
          <Link
            key={item.to}
            to={item.to}
            className={clsx(styles.item, isActive && styles.itemActive)}
            aria-current={isActive ? 'page' : undefined}
          >
            {item.icon ? (
              <span className={styles.itemIcon} aria-hidden>
                {item.icon}
              </span>
            ) : null}
            {item.label}
          </Link>
        )
      })}
    </nav>
  )
}

PageTabs.displayName = 'PageTabs'
